import React, { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Mousewheel } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { ModuleItemInterface, TabInterface } from '../../../types/home.type';
import MovieCard from '../MovieCard';
import NotFoundList from '../NotFoundList';
import i18n from '../../../config/i18n';

interface TabsWithListSectionProps {
  module: ModuleItemInterface;
}

const ITEMS_PER_PAGE = 10;

const TabsWithListSection: React.FC<TabsWithListSectionProps> = ({ module }) => {
  const tabs = (module.items || []) as TabInterface[];
  const [activeTab, setActiveTab] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [showAllTabs, setShowAllTabs] = useState<boolean>(false);
  
  // Calculate items to show based on current page
  const allItems = tabs[activeTab]?.items || [];
  const tabContent = allItems.slice(0, page * ITEMS_PER_PAGE);
  const hasMore = tabContent.length < allItems.length;
  
  const handleLoadMore = useCallback(() => {
    if (hasMore) {
      setPage(prev => prev + 1);
    }
  }, [hasMore]);

  const handleTabChange = (idx: number) => {
    setActiveTab(idx);
    setPage(1); // Reset pagination when changing tabs
  };

  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  
  // Get current language or use fallback
  const currentLang = lang || window.location.hash.split('/')[1] || i18n.options.fallbackLng;

  return (
    <div className="tabs-with-list-section" style={{ backgroundColor: module.backgroundColor || 'transparent' }}>
      <h2 className="text-xl font-bold mb-2">{module.label}</h2>
      {/* Tabs Navigation */}
      <div data-attr="scroll-container" className="nav-tabs !my-4 bg-transparent">
        <Swiper
          spaceBetween={10}
          slidesPerView={'auto'}
          className="swiper-container"
        >
          {(showAllTabs  ? tabs : tabs.slice(0, 5)).map((tab: TabInterface, idx) => (
            <SwiperSlide key={tab.id || idx} style={{ width: 'auto' }}>
              <button
                onClick={() => handleTabChange(idx)}
                className={`px-4 py-2 rounded-full ${
                  activeTab === idx
                    ? 'bg-white text-primary border font-bold'
                    : 'bg-transparent text-white font-medium'
                }`}
              >
                {tab.name || 'Tab'}
              </button>
            </SwiperSlide>
          ))}
          {!showAllTabs && tabs.length > 5 && (
            <SwiperSlide style={{ width: 'auto' }}>
              <button
                onClick={() => setShowAllTabs(true)}
                className="px-4 py-2 bg-transparent text-white font-medium rounded-full"
              >
                More
              </button>
            </SwiperSlide>
          )}
        </Swiper>
      </div>
      {/* Tab Content */}
      <div className="tab-content mx-4">
        {tabContent.length > 0 ? (
          <Swiper 
            spaceBetween={10}
            slidesPerView={'auto'}
            mousewheel={{ forceToAxis: true }} // chỉ cho vuốt theo trục ngang
            modules={[Mousewheel]}
            className="swiper-container"
            onReachEnd={() => handleLoadMore()}
          >
            {tabContent.map((item: any, idx: number) => (
              <SwiperSlide key={item.id || idx} style={{ width: '240px' }}>
                <MovieCard 
                  movie={item} 
                  size='large' 
                  onClick={() => {
                    const path = `/${currentLang}/${item.posttype || 'movie'}/${item.slug}`;
                    navigate(path, { state: { item } });
                    window.scrollTo(0, 0);
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <NotFoundList
            title="Không có danh sách nào"
            description="Danh sách này hiện không có nội dung."
        />
        )}
      </div>
    </div>
  );
};

export default TabsWithListSection;
